/**
 * Single place for “how much is free” vs ledger balance (main unchanged on bet; exposure reserves liability).
 */

export type LedgerBalanceParts = {
  balance?: number
  exposer?: number
  casinoexposer?: number
  bonusBalance?: number
  pendingBonusUsed?: number
  availableBalance?: number
}

export function totalExposureFromParts(b: LedgerBalanceParts): number {
  return (Number(b.exposer) || 0) + (Number(b.casinoexposer) || 0)
}

/** Main ledger − total exposure. Bonus wallet is separate — not included (admin + client “available”). */
export function computeWithdrawableMainFromLedger(b: LedgerBalanceParts): number {
  const main = Number(b.balance) || 0
  return Math.max(0, Math.round((main - totalExposureFromParts(b)) * 100) / 100)
}

/** Alias for list-clients / ledger: same as main available after exposure (no bonus). */
export function computeAvailableForBetFromLedger(b: LedgerBalanceParts): number {
  return computeWithdrawableMainFromLedger(b)
}

/** Client Redux: `exposer` already includes casino. `bonusBalance` ignored for display — shown only in bonus UI. */
export function computeAvailableForBetCombinedExposer(b: {
  balance: number
  exposer: number
  bonusBalance?: number
}): number {
  return computeWithdrawableMainCombinedExposer({ balance: b.balance, exposer: b.exposer })
}

export function computeWithdrawableMainCombinedExposer(b: {
  balance: number
  exposer: number
  pendingBonusUsed?: number
  availableBalance?: number
}): number {
  return computeRemainingBalance(b)
}

/**
 * Remaining main wallet the user can use (header / withdraw / bet checks):
 * balance − exposure + pendingBonusUsed (stake already paid from bonus wallet).
 * Prefer server `availableBalance` when present.
 */
export function computeRemainingBalance(b: {
  balance?: number
  exposer?: number
  casinoexposer?: number
  pendingBonusUsed?: number
  availableBalance?: number
}): number {
  if (typeof b.availableBalance === 'number' && Number.isFinite(b.availableBalance)) {
    return Math.max(0, Math.round(b.availableBalance * 100) / 100)
  }
  const main = Number(b.balance) || 0
  const exp = totalExposureFromParts(b)
  const fromBonus = Math.max(0, Number(b.pendingBonusUsed) || 0)
  return Math.max(0, Math.round((main - exp + fromBonus) * 100) / 100)
}

type WalletLike = {
  balance: number
  exposer: number
  bonusBalance?: number
  /** Unsettled PnL since last transfer (Winnings / Take-Give). */
  profitLoss?: number
}

/** True when creditRefrences was set (including intentional 0). */
function hasCreditLineValue(creditRefrences: number | string | undefined | null): boolean {
  if (creditRefrences == null || creditRefrences === '') return false
  return Number.isFinite(Number(creditRefrences))
}

/**
 * Accounts panel / “Credit Limit”:
 * - If creditRefrences is set (including 0): show that value
 * - Else fall back to ledger balance
 */
export function computeCreditLimitDisplay(
  creditRefrences: number | string | undefined | null,
  ledgerBalance: number,
): number {
  if (hasCreditLineValue(creditRefrences)) {
    return Math.round(Number(creditRefrences) * 100) / 100
  }
  return Math.max(0, Math.round((Number(ledgerBalance) || 0) * 100) / 100)
}

/**
 * Accounts panel / admin listing “Available Credit”:
 * - If credit line is set (including 0): Credit Limit + profitLoss − Net Exposure
 *   e.g. CL 20000, loss −2000 → 18000; CL 0 + loss −2000 → 0; CL 5000 + loss −2000 → 3000
 * - If credit line unset: main ledger − exposure
 */
export function computeAvailableCreditForAccountPanel(
  creditRefrences: number | string | undefined | null,
  wallet: WalletLike,
): number {
  const exp = Number(wallet.exposer) || 0
  const pl = Number(wallet.profitLoss) || 0
  if (hasCreditLineValue(creditRefrences)) {
    const cr = Number(creditRefrences)
    return Math.max(0, Math.round((cr + pl - exp) * 100) / 100)
  }
  return computeWithdrawableMainFromLedger({
    balance: wallet.balance,
    exposer: wallet.exposer,
    casinoexposer: 0,
  })
}

/**
 * Max total net exposure allowed under credit line: CL + Winnings.
 * null = no credit line (ledger-only betting checks).
 */
export function maxExposureUnderCreditLine(
  creditRefrences: number | string | undefined | null,
  profitLoss: number,
): number | null {
  if (!hasCreditLineValue(creditRefrences)) return null
  return Math.round((Number(creditRefrences) + Number(profitLoss || 0)) * 100) / 100
}

/** Whether placing a bet that results in totalExposure would breach Available Credit. */
export function exceedsCreditLineExposure(
  creditRefrences: number | string | undefined | null,
  profitLoss: number,
  totalExposure: number,
): boolean {
  const cap = maxExposureUnderCreditLine(creditRefrences, profitLoss)
  if (cap == null) return false
  return Number(totalExposure) > cap + 1e-6
}
